import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"
import { ThemeProvider } from "styled-components"
import theme from "styles/theme"
import GlobalStyles from "styles/GlobalStyles"
import Navigation from "components/molecules/Navigation"
import { PrivacyControls } from "components/molecules/PrivacyConsent"
import { PublicSiteNotice } from "components/molecules/PublicInformation"
import Footer from "components/molecules/Footer"
import GlobalBanner from "components/molecules/GlobalBanner"
import browserWindow from "utils/browserWindow"
import getGlobalAlert from "utils/getGlobalAlert"
import "pure-react-carousel/dist/react-carousel.es.css"
import { StyledMain, StyledBannerWrapper, SkipLink } from "./Layout.styles"

const Layout = ({
  children,
  isReverseColor,
  language,
  blockScrollAnimationOn,
  hideFooter,
  isNavStatic,
  setIsBannerActive,
}) => {
  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
    }
  `)
  const bannerRef = useRef(null)

  const [isGlobalBannerVisible, setIsGlobalBannerVisible] = useState(false)
  const [globalAlertData, setGlobalAlertData] = useState(null)
  const [bannerHeight, setBannerHeight] = useState(0)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const getAlertData = async () => {
    const response = await getGlobalAlert(language)

    const { globalAlertBanner } = response.data

    setGlobalAlertData(globalAlertBanner)
    setIsGlobalBannerVisible(globalAlertBanner.isPublished)
  }

  useEffect(() => {
    if (isGlobalBannerVisible) setIsBannerActive(true)
  }, [isGlobalBannerVisible])

  useEffect(() => {
    const isBannerClicked =
      browserWindow.sessionStorage.getItem("langBannerClicked")
    if (isBannerClicked === "true") {
      return
    }
    getAlertData()
  }, [])

  useEffect(() => {
    if (bannerRef) {
      setBannerHeight(bannerRef.current.offsetHeight)
    }
  }, [bannerRef, isGlobalBannerVisible, isMobileMenuOpen])

  const handleCloseGlobalBanner = () => {
    setIsGlobalBannerVisible(false)
    setIsBannerActive(false)
  }

  return (
    <ThemeProvider theme={theme}>
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <GlobalStyles />
      <StyledBannerWrapper ref={bannerRef}>
        {globalAlertData && isGlobalBannerVisible && !isMobileMenuOpen && (
          <GlobalBanner
            handleClose={handleCloseGlobalBanner}
            alertData={globalAlertData}
          />
        )}
      </StyledBannerWrapper>
      <Navigation
        siteTitle={data.site.siteMetadata.title}
        isReverseColor={isReverseColor}
        blockScrollAnimationOn={blockScrollAnimationOn}
        isNavStatic={isNavStatic}
        bannerHeight={bannerHeight}
        onMenuAction={setIsMobileMenuOpen}
      />
      <StyledMain id="main-content" bannerHeight={bannerHeight}>
        {children &&
          children.map((child) => {
            if (child) {
              const newProps = { ...child.props, bannerHeight }
              const preparedChild = { ...child, props: newProps }
              return preparedChild
            }
            return child
          })}
      </StyledMain>
      <PublicSiteNotice />
      {!hideFooter && <Footer />}
      <PrivacyControls />
    </ThemeProvider>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  isReverseColor: PropTypes.bool,
  hideFooter: PropTypes.bool,
  blockScrollAnimationOn: PropTypes.shape({
    mobileLandscape: PropTypes.bool,
    mobilePortrait: PropTypes.bool,
    desktop: PropTypes.bool,
  }),
  isNavStatic: PropTypes.bool,
  language: PropTypes.string,
  setIsBannerActive: PropTypes.func,
}

Layout.defaultProps = {
  isReverseColor: false,
  hideFooter: false,
  blockScrollAnimationOn: {
    mobileLandscape: false,
    mobilePortrait: false,
    desktop: false,
  },
  isNavStatic: false,
  language: "",
  setIsBannerActive: () => {},
}

export default Layout
